import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  supabaseConfigured,
  consumeAuthRedirect,
  getCurrentUser,
  signInWithPassword,
  signUpAccount,
  sendPasswordReset,
  updatePassword,
  updateAccountMetadata,
  signOut as accountSignOut,
  onAuthStateChange,
  restRpc,
} from '@/lib/supabase';
import { navigate } from '@/hooks/useRoute';
import { useStore } from '@/context/StoreContext';
import '../account-luxury-v1.css';
import '../account-orders-v4.css';

type Mode = 'login' | 'register' | 'forgot' | 'recovery';
type AccountPanel = 'profile' | 'addresses' | 'orders' | 'benefits' | 'payment' | null;
type ShippingAddress = {
  id:string;
  label:string;
  city:string;
  street:string;
  house:string;
  apartment:string;
  zip:string;
};


type AccountPaymentStatus='payment_pending'|'paid'|'payment_failed'|'cancelled';
type AccountFulfillmentStatus='received'|'processing'|'packing'|'shipped'|'delivered';
type AccountOrder={
  id:string;
  order_number:string;
  status:AccountPaymentStatus;
  total:number;
  currency:string;
  items:Array<{id?:number;name?:string;sku?:string;qty?:number;unitPrice?:number;lineTotal?:number;options?:Record<string,string>}>|null;
  shipping:{fulfillment?:string;street?:string;city?:string;zip?:string}|null;
  created_at:string;
  paid_at:string|null;
  fulfillment_status:AccountFulfillmentStatus;
  tracking_carrier:string|null;
  tracking_number:string|null;
  tracking_url:string|null;
  fulfillment_updated_at:string|null;
};

const accountMoney=(value:number)=>new Intl.NumberFormat('he-IL',{style:'currency',currency:'ILS',maximumFractionDigits:0}).format(Number(value||0));
const accountDate=(value:string)=>new Intl.DateTimeFormat('he-IL',{dateStyle:'medium',timeStyle:'short'}).format(new Date(value));
const paymentLabel:Record<AccountPaymentStatus,string>={
  paid:'שולם',payment_pending:'ממתין לתשלום',payment_failed:'התשלום נכשל',cancelled:'בוטל'
};
const fulfillmentLabel:Record<AccountFulfillmentStatus,string>={
  received:'ההזמנה התקבלה',processing:'בטיפול',packing:'בהכנה ואריזה',shipped:'נשלחה',delivered:'נמסרה'
};
const fulfillmentSteps:Array<{key:AccountFulfillmentStatus;label:string}>=[
  {key:'received',label:'התקבלה'},
  {key:'processing',label:'בטיפול'},
  {key:'packing',label:'בהכנה'},
  {key:'shipped',label:'נשלחה'},
  {key:'delivered',label:'נמסרה'},
];

const UserIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 12.1a4.15 4.15 0 1 0 0-8.3 4.15 4.15 0 0 0 0 8.3Z"/>
    <path d="M4.4 20.2c.55-3.55 3.34-5.72 7.6-5.72s7.05 2.17 7.6 5.72"/>
  </svg>
);

const BagIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5.4 8.1h13.2l-.8 12H6.2l-.8-12Z"/>
    <path d="M8.6 8.1V6.5a3.4 3.4 0 0 1 6.8 0v1.6"/>
  </svg>
);

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20.4 5.8c-2.15-2.2-5.7-1.65-8.4 1.05C9.3 4.15 5.75 3.6 3.6 5.8 1.15 8.3 2.05 12.05 4.5 14.55L12 21l7.5-6.45c2.45-2.5 3.35-6.25.9-8.75Z"/>
  </svg>
);

const PinIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 21s6-5.6 6-11a6 6 0 1 0-12 0c0 5.4 6 11 6 11Z"/>
    <circle cx="12" cy="10" r="2.1"/>
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3 19 6v5.6c0 4.35-2.7 7.65-7 9.4-4.3-1.75-7-5.05-7-9.4V6l7-3Z"/>
    <path d="m9.1 12 1.9 1.9 4-4"/>
  </svg>
);

export function AccountPage(){
  const { wishlist } = useStore();
  const [mode,setMode]=useState<Mode>('login');
  const [loading,setLoading]=useState(true);
  const [busy,setBusy]=useState(false);
  const [user,setUser]=useState<any>(null);
  const [message,setMessage]=useState('');
  const [error,setError]=useState('');

  const [email,setEmail]=useState('');
  const [password,setPassword]=useState('');
  const [confirmPassword,setConfirmPassword]=useState('');
  const [fullName,setFullName]=useState('');
  const [phone,setPhone]=useState('');
  const [acceptTerms,setAcceptTerms]=useState(false);

  const [accountPanel,setAccountPanel]=useState<AccountPanel>(null);
  const [profileName,setProfileName]=useState('');
  const [profilePhone,setProfilePhone]=useState('');
  const [panelMessage,setPanelMessage]=useState('');
  const [panelError,setPanelError]=useState('');
  const [addressDraft,setAddressDraft]=useState<Omit<ShippingAddress,'id'>>({
    label:'בית',
    city:'',
    street:'',
    house:'',
    apartment:'',
    zip:''
  });

  const [accountOrders,setAccountOrders]=useState<AccountOrder[]>([]);
  const [ordersLoading,setOrdersLoading]=useState(false);
  const [ordersError,setOrdersError]=useState('');

  useEffect(()=>{
    let alive=true;

    if(!supabaseConfigured){
      setError('חיבור Supabase אינו מוגדר כרגע.');
      setLoading(false);
      return;
    }

    const syncAuth=async()=>{
      try{
        const redirect=await consumeAuthRedirect();
        if(!alive)return;

        if(redirect.type==='recovery'){
          setMode('recovery');
        }

        const current=await getCurrentUser();
        if(!alive)return;

        setUser(current||null);
        if(current?.email)setEmail(current.email);
      }catch{
        if(!alive)return;
      }finally{
        if(alive)setLoading(false);
      }
    };

    void syncAuth();

    const unsubscribe=onAuthStateChange(()=>{
      void syncAuth();
    });

    return()=>{
      alive=false;
      unsubscribe();
    };
  },[]);

  const displayName=useMemo(()=>{
    const raw=user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'לקוח/ה';
    return String(raw).trim();
  },[user]);

  const shippingAddresses=useMemo<ShippingAddress[]>(()=>{
    const raw=user?.user_metadata?.shipping_addresses;
    return Array.isArray(raw) ? raw.filter(Boolean) as ShippingAddress[] : [];
  },[user]);


  const loadMyOrders=useCallback(async(silent=false)=>{
    if(!user?.id){
      setAccountOrders([]);
      return;
    }
    if(!silent)setOrdersLoading(true);
    setOrdersError('');
    try{
      const rows=await restRpc<AccountOrder[]>('mielley_my_orders',{});
      setAccountOrders(Array.isArray(rows)?rows:[]);
    }catch(err:any){
      setOrdersError(String(err?.message||'לא הצלחנו לטעון את ההזמנות כרגע.'));
    }finally{
      if(!silent)setOrdersLoading(false);
    }
  },[user?.id]);

  useEffect(()=>{
    if(user?.id){
      void loadMyOrders();
      const timer=window.setInterval(()=>{
        if(document.visibilityState==='visible')void loadMyOrders(true);
      },45000);
      return()=>window.clearInterval(timer);
    }
    setAccountOrders([]);
  },[user?.id,loadMyOrders]);

  useEffect(()=>{
    if(!user)return;
    setProfileName(String(user?.user_metadata?.full_name || user?.email?.split('@')[0] || '').trim());
    setProfilePhone(String(user?.user_metadata?.phone || '').trim());
  },[user]);

  const resetFeedback=()=>{
    setMessage('');
    setError('');
  };

  const submitLogin=async(e:FormEvent)=>{
    e.preventDefault();
    resetFeedback();

    if(!supabaseConfigured){
      setError('חיבור Supabase אינו מוגדר כרגע.');
      return;
    }

    setBusy(true);
    try{
      const {error:authError}=await signInWithPassword(email.trim(),password);
      if(authError)throw authError;

      const current=await getCurrentUser();
      setUser(current||null);
      if(current?.email)setEmail(current.email);

      setMessage('התחברת בהצלחה. ברוכה הבאה ל־MIELLEY.');
    }catch(err:any){
      const msg=String(err?.message||'');
      setError(
        msg.toLowerCase().includes('invalid')
          ?'האימייל או הסיסמה אינם נכונים.'
          :'לא הצלחנו להתחבר כרגע. בדקי את הפרטים ונסי שוב.'
      );
    }finally{
      setBusy(false);
    }
  };

  const submitRegister=async(e:FormEvent)=>{
    e.preventDefault();
    resetFeedback();

    if(!supabaseConfigured){
      setError('חיבור Supabase אינו מוגדר כרגע.');
      return;
    }

    if(password.length<8){
      setError('הסיסמה צריכה להכיל לפחות 8 תווים.');
      return;
    }
    if(password!==confirmPassword){
      setError('הסיסמאות אינן תואמות.');
      return;
    }
    if(!acceptTerms){
      setError('יש לאשר את תנאי השימוש ומדיניות הפרטיות.');
      return;
    }

    setBusy(true);
    try{
      const redirectTo=`${window.location.origin}/account`;
      const {data,error:authError}=await signUpAccount(
        email.trim(),
        password,
        {
          full_name:fullName.trim(),
          phone:phone.trim(),
          first_order_discount_eligible:true
        },
        redirectTo
      );

      if(authError)throw authError;

      if(data?.access_token){
        const current=await getCurrentUser();
        setUser(current||data?.user||null);
        setMessage('החשבון נוצר בהצלחה ואת מחוברת.');
      }else{
        setMessage('החשבון נוצר. שלחנו אלייך מייל לאימות הכתובת — לאחר האימות תוכלי להתחבר.');
        setMode('login');
      }
    }catch(err:any){
      const msg=String(err?.message||'').toLowerCase();
      setError(
        msg.includes('already') || msg.includes('registered')
          ?'כבר קיים חשבון עם כתובת האימייל הזו.'
          :'לא הצלחנו ליצור את החשבון כרגע. נסי שוב בעוד רגע.'
      );
    }finally{
      setBusy(false);
    }
  };

  const submitForgot=async(e:FormEvent)=>{
    e.preventDefault();
    resetFeedback();

    if(!supabaseConfigured){
      setError('חיבור Supabase אינו מוגדר כרגע.');
      return;
    }

    setBusy(true);
    try{
      const {error:authError}=await sendPasswordReset(
        email.trim(),
        `${window.location.origin}/account`
      );
      if(authError)throw authError;
      setMessage('שלחנו קישור לאיפוס הסיסמה. בדקי את תיבת הדואר שלך.');
    }catch{
      setError('לא הצלחנו לשלוח קישור כרגע. ודאי שהאימייל תקין ונסי שוב.');
    }finally{
      setBusy(false);
    }
  };

  const submitRecovery=async(e:FormEvent)=>{
    e.preventDefault();
    resetFeedback();

    if(!supabaseConfigured){
      setError('חיבור Supabase אינו מוגדר כרגע.');
      return;
    }

    if(password.length<8){
      setError('הסיסמה החדשה צריכה להכיל לפחות 8 תווים.');
      return;
    }
    if(password!==confirmPassword){
      setError('הסיסמאות אינן תואמות.');
      return;
    }

    setBusy(true);
    try{
      const {error:authError}=await updatePassword(password);
      if(authError)throw authError;

      setMessage('הסיסמה עודכנה בהצלחה.');
      setMode('login');
      setPassword('');
      setConfirmPassword('');
    }catch{
      setError('לא הצלחנו לעדכן את הסיסמה. פתחי שוב את קישור האיפוס שקיבלת במייל.');
    }finally{
      setBusy(false);
    }
  };

  const signOut=async()=>{
    setBusy(true);
    await accountSignOut();
    setBusy(false);
    setUser(null);
    setMode('login');
    setMessage('');
    setError('');
  };

  const closeAccountPanel=()=>{
    setAccountPanel(null);
    setPanelMessage('');
    setPanelError('');
  };

  const openAccountPanel=(panel:AccountPanel)=>{
    setPanelMessage('');
    setPanelError('');
    setAccountPanel(panel);
    if(panel==='orders')void loadMyOrders();
  };

  const refreshAccountUser=async()=>{
    const current=await getCurrentUser();
    if(current){
      setUser(current);
      if(current.email)setEmail(current.email);
    }
    return current;
  };

  const saveProfile=async(e:FormEvent)=>{
    e.preventDefault();
    setPanelMessage('');
    setPanelError('');

    if(!profileName.trim()){
      setPanelError('יש להזין שם.');
      return;
    }

    setBusy(true);
    try{
      const {error:updateError}=await updateAccountMetadata({
        full_name:profileName.trim(),
        phone:profilePhone.trim()
      });
      if(updateError)throw updateError;

      await refreshAccountUser();
      setPanelMessage('הפרטים נשמרו בהצלחה ✦');
    }catch{
      setPanelError('לא הצלחנו לשמור את הפרטים כרגע. נסי שוב.');
    }finally{
      setBusy(false);
    }
  };

  const saveAddress=async(e:FormEvent)=>{
    e.preventDefault();
    setPanelMessage('');
    setPanelError('');

    if(!addressDraft.city.trim() || !addressDraft.street.trim() || !addressDraft.house.trim()){
      setPanelError('יש למלא עיר, רחוב ומספר בית.');
      return;
    }

    const nextAddress:ShippingAddress={
      id:(globalThis.crypto?.randomUUID?.() || `addr-${Date.now()}`),
      label:addressDraft.label.trim() || 'בית',
      city:addressDraft.city.trim(),
      street:addressDraft.street.trim(),
      house:addressDraft.house.trim(),
      apartment:addressDraft.apartment.trim(),
      zip:addressDraft.zip.trim()
    };

    setBusy(true);
    try{
      const {error:updateError}=await updateAccountMetadata({
        shipping_addresses:[...shippingAddresses,nextAddress]
      });
      if(updateError)throw updateError;

      await refreshAccountUser();
      setAddressDraft({label:'בית',city:'',street:'',house:'',apartment:'',zip:''});
      setPanelMessage('הכתובת נוספה לחשבון ✦');
    }catch{
      setPanelError('לא הצלחנו לשמור את הכתובת כרגע. נסי שוב.');
    }finally{
      setBusy(false);
    }
  };

  const removeAddress=async(id:string)=>{
    setPanelMessage('');
    setPanelError('');
    setBusy(true);
    try{
      const next=shippingAddresses.filter(address=>address.id!==id);
      const {error:updateError}=await updateAccountMetadata({shipping_addresses:next});
      if(updateError)throw updateError;

      await refreshAccountUser();
      setPanelMessage('הכתובת הוסרה.');
    }catch{
      setPanelError('לא הצלחנו להסיר את הכתובת כרגע.');
    }finally{
      setBusy(false);
    }
  };


  if(loading){
    return <section className="macc-loading" dir="rtl">
      <div className="macc-spinner"/>
      <span>מכינים את האזור האישי…</span>
    </section>;
  }

  if(user){
    const firstInitial=(displayName||'M').slice(0,1).toUpperCase();
    const showFirstOrderBenefit=user?.user_metadata?.first_order_discount_eligible===true;

    return (
      <main className="macc-page macc-dashboard macc-magic-dashboard" dir="rtl">
        <section className="macc-magic-hero">
          <div className="macc-magic-hero-visual" aria-hidden="true"/>
          <div className="macc-magic-sparkles" aria-hidden="true">
            <span>✦</span><span>◇</span><span>✧</span><span>✦</span>
          </div>

          <div className="macc-shell macc-magic-hero-inner">
            <div className="macc-magic-welcome">
              <span className="macc-magic-kicker">MIELLEY PRIVATE CLIENT</span>

              <div className="macc-magic-title-row">
                <div className="macc-magic-avatar">
                  <b>{firstInitial}</b>
                  <i>◇</i>
                </div>

                <div>
                  <h1>שלום, {displayName}</h1>
                  <p>כיף שחזרת לעולם הקסום שלך ב־MIELLEY.</p>
                  <small>{user.email}</small>
                </div>
              </div>

              <div className="macc-magic-status">
                <span>✦</span>
                <strong>PRIVATE CLIENT</strong>
                <em>חשבון אישי ומאובטח</em>
              </div>
            </div>
          </div>

          <div className="macc-magic-hero-diamond" aria-hidden="true">◇</div>
        </section>

        {showFirstOrderBenefit&&(
          <section className="macc-shell macc-new-client-benefit" aria-label="הטבת הצטרפות">
            <div className="macc-new-client-benefit-mark">
              <span>NEW CLIENT BENEFIT</span>
              <strong>10%</strong>
            </div>

            <div className="macc-new-client-benefit-copy">
              <span className="macc-new-client-benefit-kicker">מתנה קטנה מאיתנו ✦</span>
              <h2>יש לך 10% הנחה לקנייה הראשונה</h2>
              <p>ברוכה הבאה ל־MIELLEY. ההטבה האישית שלך כבר מחכה לך.</p>
            </div>

            <button
              type="button"
              className="macc-new-client-benefit-cta"
              onClick={()=>navigate('/catalog')}
            >
              מימוש ההטבה
              <span>←</span>
            </button>

            <div className="macc-new-client-benefit-sparkles" aria-hidden="true">
              <i>✦</i><i>◇</i><i>✧</i>
            </div>
          </section>
        )}

        <section className="macc-shell macc-magic-content">
          <div className="macc-magic-primary-grid">
            <article className="macc-magic-card macc-magic-profile">
              <div className="macc-magic-card-head">
                <i><UserIcon/></i>
                <div>
                  <span>הפרטים האישיים שלי</span>
                  <strong>{displayName}</strong>
                </div>
              </div>

              <div className="macc-magic-profile-lines">
                <p>{user.email}</p>
                {user.user_metadata?.phone&&<p>{user.user_metadata.phone}</p>}
              </div>

              <button type="button" className="macc-magic-outline" onClick={()=>openAccountPanel('profile')}>
                עריכת פרטים
              </button>
            </article>

            <article className="macc-magic-card macc-magic-favorites">
              <div className="macc-magic-card-head">
                <i><HeartIcon/></i>
                <div>
                  <span>המועדפים שלי</span>
                  <strong>{wishlist.length}</strong>
                </div>
              </div>

              <p>כל התכשיטים שאהבת ושמרת מחכים לך כאן.</p>

              <button
                type="button"
                className="macc-magic-outline"
                onClick={()=>navigate('/wishlist')}
              >
                למועדפים שלי
              </button>
            </article>

            <article className="macc-magic-card macc-magic-orders">
              <div className="macc-magic-card-head">
                <i><BagIcon/></i>
                <div>
                  <span>ההזמנות שלי</span>
                  <strong>{ordersLoading?'…':accountOrders.length}</strong>
                </div>
              </div>

              <p>מעקב, סטטוס והיסטוריית רכישות במקום אחד.</p>

              <button type="button" className="macc-magic-outline" onClick={()=>openAccountPanel('orders')}>
                צפייה בהזמנות
              </button>
            </article>
          </div>

          <div className="macc-magic-secondary-grid">
            <article className="macc-magic-card macc-magic-benefits-card">
              <div className="macc-magic-section-title">
                <span>✧</span>
                <strong>ההטבות שלי</strong>
                <small>PRIVATE CLIENT</small>
              </div>

              <div className="macc-magic-benefits">
                <div><i>▱</i><b>משלוח חינם</b><span>על כל הזמנה</span></div>
                <div><i>↻</i><b>גישה מוקדמת</b><span>לקולקציות חדשות</span></div>
                <div><i>◇</i><b>מבצעים בלעדיים</b><span>לחברות המועדון</span></div>
                <div><i>🎁</i><b>הפתעות והטבות</b><span>במהלך השנה</span></div>
              </div>

              <button type="button" className="macc-magic-outline" onClick={()=>openAccountPanel('benefits')}>
                כל ההטבות שלי
              </button>
            </article>

            <article className="macc-magic-card macc-magic-address-card">
              <div className="macc-magic-card-head">
                <i><PinIcon/></i>
                <div>
                  <span>כתובות משלוח</span>
                  <strong>{shippingAddresses.length}</strong>
                </div>
              </div>
              <p>שמרי כתובות למשלוח מהיר ונוח יותר.</p>
              <button type="button" className="macc-magic-outline" onClick={()=>openAccountPanel('addresses')}>
                ניהול כתובות
              </button>
            </article>

            <article className="macc-magic-card macc-magic-payment-card">
              <div className="macc-magic-card-head">
                <i className="macc-card-symbol">▭</i>
                <div>
                  <span>אמצעי תשלום</span>
                  <strong>—</strong>
                </div>
              </div>
              <p>ניהול אמצעי תשלום שמורים יתווסף בהמשך.</p>
              <button type="button" className="macc-magic-outline" onClick={()=>openAccountPanel('payment')}>
                מידע על תשלום מאובטח
              </button>
            </article>
          </div>

          <div className="macc-magic-footer-strip">
            <div className="macc-magic-footer-copy">
              <span>◇</span>
              <div>
                <strong>אנחנו כאן בשבילך</strong>
                <small>שירות אישי, ליווי וחוויית MIELLEY בכל שלב.</small>
              </div>
            </div>

            <div className="macc-magic-service">
              <i>✦</i>
              <div><strong>שירות לקוחות אישי</strong><small>אנחנו כאן עבורך</small></div>
            </div>

            <div className="macc-magic-service">
              <i>↻</i>
              <div><strong>החזרות קלות</strong><small>עד 30 יום</small></div>
            </div>

            <div className="macc-magic-service">
              <i>▱</i>
              <div><strong>משלוח חינם</strong><small>בקנייה מעל הסכום המזכה</small></div>
            </div>

            <div className="macc-magic-service">
              <i><ShieldIcon/></i>
              <div><strong>חשבון מאובטח</strong><small>הפרטיות שלך חשובה לנו</small></div>
            </div>
          </div>

          {accountPanel&&(
            <div
              className="macc-account-modal-backdrop"
              role="presentation"
              onMouseDown={e=>{if(e.currentTarget===e.target)closeAccountPanel();}}
            >
              <section
                className="macc-account-modal"
                role="dialog"
                aria-modal="true"
                aria-label="האזור האישי"
              >
                <button
                  type="button"
                  className="macc-account-modal-close"
                  onClick={closeAccountPanel}
                  aria-label="סגירה"
                >
                  ×
                </button>

                {panelMessage&&<div className="macc-account-panel-alert success">{panelMessage}</div>}
                {panelError&&<div className="macc-account-panel-alert error">{panelError}</div>}

                {accountPanel==='profile'&&(
                  <>
                    <div className="macc-account-modal-title">
                      <i><UserIcon/></i>
                      <div>
                        <span>החשבון שלי</span>
                        <h2>עריכת פרטים אישיים</h2>
                        <p>עדכני את השם והטלפון שיופיעו באזור האישי שלך.</p>
                      </div>
                    </div>

                    <form className="macc-account-panel-form" onSubmit={saveProfile}>
                      <label>
                        <span>שם מלא</span>
                        <input
                          value={profileName}
                          onChange={e=>setProfileName(e.target.value)}
                          autoComplete="name"
                          required
                        />
                      </label>

                      <label>
                        <span>טלפון</span>
                        <input
                          value={profilePhone}
                          onChange={e=>setProfilePhone(e.target.value)}
                          autoComplete="tel"
                          inputMode="tel"
                          placeholder="05X-XXXXXXX"
                        />
                      </label>

                      <label className="macc-account-readonly">
                        <span>אימייל</span>
                        <input value={String(user.email||'')} readOnly/>
                      </label>

                      <button className="macc-account-save" disabled={busy}>
                        {busy?'שומרת…':'שמירת השינויים'}
                      </button>
                    </form>
                  </>
                )}

                {accountPanel==='addresses'&&(
                  <>
                    <div className="macc-account-modal-title">
                      <i><PinIcon/></i>
                      <div>
                        <span>משלוחים</span>
                        <h2>כתובות המשלוח שלי</h2>
                        <p>שמרי כתובת כדי שההזמנה הבאה תהיה מהירה ונוחה יותר.</p>
                      </div>
                    </div>

                    {shippingAddresses.length>0&&(
                      <div className="macc-address-list">
                        {shippingAddresses.map(address=>(
                          <article key={address.id}>
                            <div>
                              <strong>{address.label || 'כתובת'}</strong>
                              <span>
                                {address.street} {address.house}
                                {address.apartment?`, דירה ${address.apartment}`:''}, {address.city}
                                {address.zip?` · ${address.zip}`:''}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={()=>removeAddress(address.id)}
                              disabled={busy}
                            >
                              הסרה
                            </button>
                          </article>
                        ))}
                      </div>
                    )}

                    <form className="macc-account-panel-form" onSubmit={saveAddress}>
                      <div className="macc-account-form-grid">
                        <label>
                          <span>שם לכתובת</span>
                          <input
                            value={addressDraft.label}
                            onChange={e=>setAddressDraft(v=>({...v,label:e.target.value}))}
                            placeholder="בית"
                          />
                        </label>
                        <label>
                          <span>עיר</span>
                          <input
                            value={addressDraft.city}
                            onChange={e=>setAddressDraft(v=>({...v,city:e.target.value}))}
                            required
                          />
                        </label>
                      </div>

                      <div className="macc-account-form-grid">
                        <label>
                          <span>רחוב</span>
                          <input
                            value={addressDraft.street}
                            onChange={e=>setAddressDraft(v=>({...v,street:e.target.value}))}
                            required
                          />
                        </label>
                        <label>
                          <span>מספר בית</span>
                          <input
                            value={addressDraft.house}
                            onChange={e=>setAddressDraft(v=>({...v,house:e.target.value}))}
                            required
                          />
                        </label>
                      </div>

                      <div className="macc-account-form-grid">
                        <label>
                          <span>דירה</span>
                          <input
                            value={addressDraft.apartment}
                            onChange={e=>setAddressDraft(v=>({...v,apartment:e.target.value}))}
                          />
                        </label>
                        <label>
                          <span>מיקוד</span>
                          <input
                            value={addressDraft.zip}
                            onChange={e=>setAddressDraft(v=>({...v,zip:e.target.value}))}
                            inputMode="numeric"
                          />
                        </label>
                      </div>

                      <button className="macc-account-save" disabled={busy}>
                        {busy?'שומרת…':'הוספת כתובת'}
                      </button>
                    </form>
                  </>
                )}

                {accountPanel==='orders'&&(
                  <>
                    <div className="macc-account-modal-title">
                      <i><BagIcon/></i>
                      <div>
                        <span>הרכישות שלי</span>
                        <h2>ההזמנות שלי</h2>
                        <p>מעקב אמיתי אחרי תשלום, טיפול ומשלוח — מהקופה ועד אלייך.</p>
                      </div>
                    </div>

                    {ordersLoading&&<div className="macc-orders-v4-loading"><span className="macc-spinner"/><b>טוענים את ההזמנות שלך…</b></div>}

                    {!ordersLoading&&ordersError&&<div className="macc-orders-v4-error"><b>לא הצלחנו לטעון את ההזמנות.</b><span>{ordersError}</span><button type="button" onClick={()=>void loadMyOrders()}>נסי שוב</button></div>}

                    {!ordersLoading&&!ordersError&&!accountOrders.length&&(
                      <div className="macc-account-empty-state">
                        <span>◇</span>
                        <strong>עדיין אין הזמנות להצגה</strong>
                        <p>אחרי הרכישה הראשונה, פרטי ההזמנה והמעקב יופיעו כאן אוטומטית.</p>
                        <button type="button" onClick={()=>navigate('/catalog')}>לצפייה בתכשיטים</button>
                      </div>
                    )}

                    {!ordersLoading&&!ordersError&&accountOrders.length>0&&(
                      <div className="macc-orders-v4-list">
                        {accountOrders.map(order=>{
                          const itemCount=order.items?.reduce((sum,item)=>sum+Number(item.qty||0),0)||0;
                          const stepIndex=fulfillmentSteps.findIndex(step=>step.key===order.fulfillment_status);
                          const paid=order.status==='paid';
                          return <article className="macc-order-v4" key={order.id}>
                            <header>
                              <div><span>הזמנה</span><strong>{order.order_number}</strong><small>{accountDate(order.created_at)} · {itemCount} פריטים</small></div>
                              <div className="macc-order-v4-total"><b>{accountMoney(order.total)}</b><span className={`macc-pay-status ${order.status}`}>{paymentLabel[order.status]}</span></div>
                            </header>

                            <div className="macc-order-v4-items">
                              {order.items?.slice(0,4).map((item,index)=><div key={`${order.id}-${index}`}><b>{item.name||`מוצר ${item.id||''}`}</b><span>{item.qty||0} × {accountMoney(Number(item.unitPrice||0))}</span></div>)}
                              {(order.items?.length||0)>4&&<small>+ {(order.items?.length||0)-4} פריטים נוספים</small>}
                            </div>

                            {paid?(
                              <>
                                <div className="macc-order-v4-current"><span>מצב ההזמנה</span><b>{fulfillmentLabel[order.fulfillment_status||'received']}</b></div>
                                <div className="macc-order-progress" aria-label={`סטטוס הזמנה: ${fulfillmentLabel[order.fulfillment_status||'received']}`}>
                                  {fulfillmentSteps.map((step,index)=><div className={index<stepIndex?'done':index===stepIndex?'active':''} key={step.key}><i>{index<=stepIndex?'✓':''}</i><span>{step.label}</span></div>)}
                                </div>
                                {(order.fulfillment_status==='shipped'||order.fulfillment_status==='delivered')&&(
                                  <div className="macc-order-tracking">
                                    <div><span>משלוח</span><b>{order.tracking_carrier||'נשלח אלייך'}</b>{order.tracking_number&&<small>מספר מעקב: {order.tracking_number}</small>}</div>
                                    {order.tracking_url&&<a href={order.tracking_url} target="_blank" rel="noreferrer">מעקב משלוח ↗</a>}
                                  </div>
                                )}
                              </>
                            ):(
                              <div className={`macc-order-payment-note ${order.status}`}><b>{paymentLabel[order.status]}</b><span>{order.status==='payment_pending'?'אנחנו ממתינים לאישור הסופי מחברת הסליקה.':order.status==='payment_failed'?'התשלום לא הושלם. אפשר לבצע הזמנה חדשה.':'ההזמנה בוטלה.'}</span></div>
                            )}
                          </article>;
                        })}
                      </div>
                    )}
                  </>
                )}

                {accountPanel==='benefits'&&(
                  <>
                    <div className="macc-account-modal-title">
                      <i className="macc-account-gift">✦</i>
                      <div>
                        <span>MIELLEY PRIVATE CLIENT</span>
                        <h2>ההטבות שלך</h2>
                        <p>חוויה אישית יותר בכל פעם שאת חוזרת ל־MIELLEY.</p>
                      </div>
                    </div>

                    <div className="macc-account-benefit-list">
                      <article><i>▱</i><div><strong>משלוח חינם</strong><span>בהזמנות שעומדות בתנאי ההטבה באתר.</span></div></article>
                      <article><i>↻</i><div><strong>גישה מוקדמת</strong><span>לקולקציות והשקות נבחרות.</span></div></article>
                      <article><i>◇</i><div><strong>מבצעים בלעדיים</strong><span>הטבות מיוחדות ללקוחות רשומים.</span></div></article>
                      <article><i>🎁</i><div><strong>הפתעות במהלך השנה</strong><span>הטבות משתנות שיחכו לך באזור האישי.</span></div></article>
                    </div>
                  </>
                )}

                {accountPanel==='payment'&&(
                  <>
                    <div className="macc-account-modal-title">
                      <i><ShieldIcon/></i>
                      <div>
                        <span>תשלום מאובטח</span>
                        <h2>אמצעי התשלום שלך</h2>
                        <p>אנחנו מעדיפים לשמור על המידע הפיננסי שלך מחוץ לאתר.</p>
                      </div>
                    </div>

                    <div className="macc-account-secure-payment">
                      <ShieldIcon/>
                      <strong>MIELLEY לא שומרת מספרי כרטיס אשראי באזור האישי.</strong>
                      <p>
                        התשלום מתבצע דרך תשתית הסליקה המאובטחת בזמן ההזמנה.
                        אם בעתיד נפעיל שמירת כרטיס באמצעות Token מאובטח של חברת הסליקה,
                        האפשרות תופיע כאן.
                      </p>
                    </div>
                  </>
                )}
              </section>
            </div>
          )}

          <div className="macc-magic-signout-row">
            <button onClick={signOut} disabled={busy}>
              {busy?'מתנתקים…':'התנתקות מהחשבון'}
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="macc-page macc-reference-page" dir="rtl">
      <header className="macc-mini-header">
        <button
          className="macc-back-store"
          type="button"
          onClick={()=>navigate('/')}
        >
          <span>←</span>
          חזרה לחנות
        </button>

        <button
          className="macc-mini-brand"
          type="button"
          onClick={()=>navigate('/')}
          aria-label="MIELLEY"
        >
          <span className="macc-brand-diamond">◇</span>
          <strong>MIELLEY</strong>
          <small>LOOK EXPENSIVE. THINK SMART.</small>
        </button>

        <span className="macc-header-spacer" />
      </header>

      <section className="macc-reference-main">
        <div className="macc-reference-deco" aria-hidden="true">
          <span className="macc-ref-spark macc-ref-spark-1">✦</span>
          <span className="macc-ref-spark macc-ref-spark-2">✧</span>
        </div>

        <div className="macc-reference-shell">
          <section className="macc-reference-story">
            <div className="macc-reference-photo" aria-hidden="true">
              <img src="/assets/mielley-account-softchic-jewelry.png" alt="" />
            </div>

            <div className="macc-reference-copy">
              <h1>ברוכה הבאה<br/><em>לעולם של MIELLEY</em></h1>

              <div className="macc-title-divider">
                <span/>
                <b>◇</b>
                <span/>
              </div>

              <p>
                צרי חשבון וגלי חוויית קנייה מותאמת אישית עבורך.
                שמרי את הפריטים שאהבת, עקבי אחרי ההזמנות שלך
                וחזרי בקלות לכל מה שבחרת — הכל במקום אחד.
              </p>

              <div className="macc-reference-perks">
                <div>
                  <i><BagIcon/></i>
                  <span>ניהול הזמנות<br/>בקלות</span>
                </div>
                <div>
                  <i><HeartIcon/></i>
                  <span>המועדפים<br/>שאהבת</span>
                </div>
                <div>
                  <i>◇</i>
                  <span>הטבות<br/>והפתעות</span>
                </div>
              </div>
            </div>
          </section>

          <section className="macc-auth-card macc-reference-auth" aria-label="כניסה והרשמה לחשבון">
            {mode!=='forgot'&&mode!=='recovery'&&(
              <div className="macc-tabs">
                <button
                  className={mode==='login'?'active':''}
                  onClick={()=>{setMode('login');resetFeedback();}}
                  type="button"
                >
                  התחברות
                </button>
                <button
                  className={mode==='register'?'active':''}
                  onClick={()=>{setMode('register');resetFeedback();}}
                  type="button"
                >
                  יצירת חשבון
                </button>
              </div>
            )}

            {message&&<div className="macc-alert success">{message}</div>}
            {error&&<div className="macc-alert error">{error}</div>}

            {mode==='login'&&(
              <form className="macc-form" onSubmit={submitLogin}>
                <label className="macc-field-with-icon">
                  <span className="macc-input-icon">✉</span>
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={e=>setEmail(e.target.value)}
                    placeholder="אימייל"
                  />
                </label>

                <label className="macc-field-with-icon">
                  <span className="macc-input-icon">◉</span>
                  <input
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={e=>setPassword(e.target.value)}
                    placeholder="סיסמה"
                  />
                </label>

                <button
                  className="macc-forgot"
                  type="button"
                  onClick={()=>{setMode('forgot');resetFeedback();}}
                >
                  שכחת סיסמה?
                </button>

                <button className="macc-primary" disabled={busy}>
                  {busy?'מתחברת…':'התחברות ✦'}
                </button>

                <div className="macc-or"><span/>או<span/></div>

                <button
                  className="macc-secondary"
                  type="button"
                  onClick={()=>{setMode('register');resetFeedback();}}
                >
                  יצירת חשבון חדש
                </button>
              </form>
            )}

            {mode==='register'&&(
              <form className="macc-form" onSubmit={submitRegister}>
                <div className="macc-form-grid">
                  <label>
                    <span>שם מלא</span>
                    <input
                      required
                      autoComplete="name"
                      value={fullName}
                      onChange={e=>setFullName(e.target.value)}
                      placeholder="השם שלך"
                    />
                  </label>

                  <label>
                    <span>טלפון</span>
                    <input
                      required
                      inputMode="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={e=>setPhone(e.target.value)}
                      placeholder="05X-XXXXXXX"
                    />
                  </label>
                </div>

                <label>
                  <span>כתובת אימייל</span>
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={e=>setEmail(e.target.value)}
                    placeholder="name@example.com"
                  />
                </label>

                <div className="macc-form-grid">
                  <label>
                    <span>סיסמה</span>
                    <input
                      type="password"
                      minLength={8}
                      autoComplete="new-password"
                      required
                      value={password}
                      onChange={e=>setPassword(e.target.value)}
                      placeholder="לפחות 8 תווים"
                    />
                  </label>

                  <label>
                    <span>אימות סיסמה</span>
                    <input
                      type="password"
                      minLength={8}
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={e=>setConfirmPassword(e.target.value)}
                      placeholder="הקלידי שוב"
                    />
                  </label>
                </div>

                <label className="macc-check">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={e=>setAcceptTerms(e.target.checked)}
                  />
                  <span>
                    קראתי ואני מאשרת את{' '}
                    <a href="/terms" onClick={e=>{e.preventDefault();navigate('/terms')}}>תנאי השימוש</a>
                    {' '}ואת{' '}
                    <a href="/privacy" onClick={e=>{e.preventDefault();navigate('/privacy')}}>מדיניות הפרטיות</a>.
                  </span>
                </label>

                <button className="macc-primary" disabled={busy}>
                  {busy?'יוצרים חשבון…':'יצירת החשבון שלי ✦'}
                </button>
              </form>
            )}

            {mode==='forgot'&&(
              <form className="macc-form" onSubmit={submitForgot}>
                <div className="macc-back-form-title">
                  <strong>איפוס סיסמה</strong>
                  <span>שלחי לעצמך קישור מאובטח לאיפוס הסיסמה.</span>
                </div>

                <label>
                  <span>כתובת אימייל</span>
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={e=>setEmail(e.target.value)}
                    placeholder="name@example.com"
                  />
                </label>

                <button className="macc-primary" disabled={busy}>
                  {busy?'שולחים…':'שליחת קישור לאיפוס'}
                </button>

                <button
                  className="macc-secondary"
                  type="button"
                  onClick={()=>{setMode('login');resetFeedback();}}
                >
                  חזרה להתחברות
                </button>
              </form>
            )}

            {mode==='recovery'&&(
              <form className="macc-form" onSubmit={submitRecovery}>
                <div className="macc-back-form-title">
                  <strong>סיסמה חדשה</strong>
                  <span>בחרי סיסמה חדשה לחשבון שלך.</span>
                </div>

                <label>
                  <span>סיסמה חדשה</span>
                  <input
                    type="password"
                    minLength={8}
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={e=>setPassword(e.target.value)}
                    placeholder="לפחות 8 תווים"
                  />
                </label>

                <label>
                  <span>אימות סיסמה חדשה</span>
                  <input
                    type="password"
                    minLength={8}
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={e=>setConfirmPassword(e.target.value)}
                    placeholder="הקלידי שוב"
                  />
                </label>

                <button className="macc-primary" disabled={busy}>
                  {busy?'מעדכנים…':'שמירת הסיסמה החדשה'}
                </button>
              </form>
            )}

            <div className="macc-discount-note">
              <span className="macc-gift-icon">🎁</span>
              <div>
                <strong>מצטרפות חדשות נהנות מ־10% הנחה</strong>
                <span>על הקנייה הראשונה!</span>
              </div>
            </div>

            <div className="macc-trust">
              <ShieldIcon/>
              <div>
                <strong>הנתונים שלך מאובטחים</strong>
                <span>אנחנו שומרים על הפרטיות שלך</span>
              </div>
            </div>
          </section>
        </div>
      </section>

      <section className="macc-reference-service-strip">
        <div>
          <span className="macc-service-icon">✦</span>
          <strong>שירות לקוחות אישי</strong>
          <small>אנחנו כאן בשבילך</small>
        </div>
        <i/>
        <div>
          <span className="macc-service-icon">◇</span>
          <strong>תכשיטים איכותיים</strong>
          <small>חומרים איכותיים ונצחיים</small>
        </div>
        <i/>
        <div>
          <span className="macc-service-icon">↻</span>
          <strong>החזרות קלות</strong>
          <small>עד 30 ימים</small>
        </div>
        <i/>
        <div>
          <span className="macc-service-icon">▱</span>
          <strong>משלוח חינם</strong>
          <small>בקנייה מעל 199 ₪</small>
        </div>
      </section>
    </main>
  );
}
